import React from "react";
import { useTranslation } from "react-i18next";
import { getRiskLevelPriority, getRiskLevelGradient, riskLevelBadgeColors } from "../../../utils/riskLevelUtils";
import { levelDisplayNames } from "../../../utils/displayNameUtils";
import { getSubcategoryLabel } from "../../../utils/categoryLabels";

// Use global risk level utilities
const badgeColors = riskLevelBadgeColors;

const SectionCard = ({ children, title, icon }) => (
  <div style={{
    background: "linear-gradient(145deg, var(--card-bg), rgba(59,130,246,0.05))",
    borderRadius: "16px",
    padding: "24px",
    border: "1px solid var(--card-border)",
    boxShadow: "0 12px 30px rgba(15,23,42,0.08)",
    display: "flex",
    flexDirection: "column",
    gap: "16px"
  }}>
    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
      <div style={{
        width: "40px",
        height: "40px",
        borderRadius: "12px",
        background: "rgba(59,130,246,0.15)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "20px",
        color: "#3b82f6"
      }}>
        {icon}
      </div>
      <h3 style={{ margin: 0, color: "var(--card-text)", fontSize: "18px" }}>{title}</h3>
    </div>
    {children}
  </div>
);

const MetricChip = ({ label, value, color }) => (
  <div style={{
    padding: "10px 14px",
    borderRadius: "999px",
    border: `1px solid ${color}33`,
    background: `${color}14`,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    flex: "1 1 0",
    minWidth: "160px"
  }}>
    <span style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--muted-text)" }}>
      {label}
    </span>
    <span style={{ fontSize: "16px", fontWeight: 600, color: "var(--card-text)" }}>
      {value}
    </span>
  </div>
);

// Use global risk level utilities - create a helper object for backward compatibility
const riskGradients = {
  get critical() { return getRiskLevelGradient("critical"); },
  get high() { return getRiskLevelGradient("high"); },
  get medium() { return getRiskLevelGradient("medium"); },
  get low() { return getRiskLevelGradient("low"); },
  get very_low() { return getRiskLevelGradient("very_low"); },
  get default() { return getRiskLevelGradient("unknown"); },
};

const ReportRenderer = ({ data }) => {
  const { t } = useTranslation("reports_recs", { keyPrefix: "reports_detailed.mcp" });
  const reportData = data || {};

  const summary = reportData.summary || {};
  const breakdownEntries = Object.entries(reportData.attack_type_breakdown || {});
  const detailedResults = Array.isArray(reportData.detailed_results) ? reportData.detailed_results : [];
  const securityFindings = Array.isArray(reportData.security_findings) ? reportData.security_findings : [];

  const formatNumber = (value) => {
    if (typeof value !== "number") return t("notAvailable");
    if (value >= 1000) return value.toLocaleString();
    return Number.isInteger(value) ? value : value.toFixed(2);
  };

  const sortedResults = [...detailedResults].sort((a, b) => {
    if (a.passed !== b.passed) {
      return a.passed ? 1 : -1;
    }
    const aRisk = getRiskLevelPriority(a.risk_level);
    const bRisk = getRiskLevelPriority(b.risk_level);
    return aRisk - bRisk;
  });

  const sortedSecurityFindings = [...securityFindings].sort((a, b) => {
    const aRisk = getRiskLevelPriority(a.risk_level);
    const bRisk = getRiskLevelPriority(b.risk_level);
    return aRisk - bRisk;
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px", padding: "10px 0 40px" }}>
      <SectionCard title={t("mcpServerTestsOverview")} icon={<i className="fas fa-shield-alt"></i>}>
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <div style={{
            background: getRiskLevelGradient(summary.overall_risk_level),
            borderRadius: "12px",
            padding: "18px",
            color: "white",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center"
          }}>
            <div style={{ opacity: 0.8, fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em" }}>{t("overallRisk")}</div>
            <div style={{ fontSize: "28px", fontWeight: 700, marginTop: "4px" }}>
              {levelDisplayNames[summary.overall_risk_level] || t("notAvailable")}
            </div>
            <div style={{ marginTop: "12px", fontSize: "14px", opacity: 0.9 }}>
              {t("successfullyBlockedTests")}: {formatNumber(summary.tests_passed)} · {t("failedToBlock")}: {formatNumber(summary.tests_failed)}
            </div>
            {reportData.final_verdict && (
              <div style={{ marginTop: "12px", fontSize: "13px", opacity: 0.95, fontWeight: 500, fontStyle: "italic" }}>
                {reportData.final_verdict}
              </div>
            )}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "16px" }}>
            <MetricChip label={t("totalCategoriesTested")} value={formatNumber(summary.total_tests)} color="#3b82f6" />
            <MetricChip label={t("totalRequests")} value={formatNumber(summary.total_requests)} color="#8b5cf6" />
            <MetricChip label={t("totalDurationSeconds")} value={formatNumber(summary.total_duration_seconds)} color="#f97316" />
            <MetricChip label={t("securityViolations")} value={formatNumber(summary.security_violations)} color="#ef4444" />
          </div>
          {(reportData.generated_at || reportData.target_url || reportData.session_id || reportData.tester) && (
            <div style={{
              borderTop: "1px solid var(--card-border)",
              paddingTop: "16px",
              marginTop: "8px"
            }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "12px" }}>
                {reportData.generated_at && (
                  <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                    <span style={{ fontSize: "11px", textTransform: "uppercase", color: "var(--muted-text)", letterSpacing: "0.08em" }}>{t("generatedAt")}</span>
                    <span style={{ fontSize: "13px", color: "var(--card-text)" }}>{reportData.generated_at}</span>
                  </div>
                )}
                {reportData.target_url && (
                  <div style={{ display: "flex", flexDirection: "column", gap: "4px", minWidth: 0 }}>
                    <span style={{ fontSize: "11px", textTransform: "uppercase", color: "var(--muted-text)", letterSpacing: "0.08em" }}>{t("targetUrl")}</span>
                    <span style={{ fontSize: "13px", color: "var(--card-text)", wordBreak: "break-all", overflowWrap: "break-word", overflow: "hidden" }}>{reportData.target_url}</span>
                  </div>
                )}
                {reportData.session_id && (
                  <div style={{ display: "flex", flexDirection: "column", gap: "4px", minWidth: 0 }}>
                    <span style={{ fontSize: "11px", textTransform: "uppercase", color: "var(--muted-text)", letterSpacing: "0.08em" }}>{t("sessionId")}</span>
                    <span style={{ fontSize: "13px", color: "var(--card-text)", fontFamily: "monospace", wordBreak: "break-all", overflowWrap: "break-word", overflow: "hidden" }}>{reportData.session_id}</span>
                  </div>
                )}
                {reportData.tester && (
                  <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                    <span style={{ fontSize: "11px", textTransform: "uppercase", color: "var(--muted-text)", letterSpacing: "0.08em" }}>{t("tester")}</span>
                    <span style={{ fontSize: "13px", color: "var(--card-text)" }}>{reportData.tester}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </SectionCard>

      <SectionCard title={t("attackTypeBreakdown")} icon={<i className="fas fa-chart-bar"></i>}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "16px" }}>
          {breakdownEntries.map(([attackType, stats], index) => (
            <div key={attackType || `breakdown-${index}`} style={{
              border: "1px solid var(--card-border)",
              borderRadius: "12px",
              padding: "16px",
              background: "var(--card-bg)",
              display: "flex",
              flexDirection: "column",
              gap: "12px"
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ fontWeight: 600, color: "var(--card-text)" }}>
                  {attackType ? getSubcategoryLabel("mcp_agent", attackType) : t("unknown")}
                </div>
                <span style={{
                  padding: "4px 10px",
                  borderRadius: "999px",
                  fontSize: "12px",
                  background: "rgba(59,130,246,0.12)",
                  color: "#2563eb",
                  fontWeight: 600
                }}>
                  {stats.count || 0} {t("testsChip")}
                </span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "13px", color: "var(--card-text)" }}>
                    <span>{t("securityReadiness")}</span>
                    <strong>{typeof stats?.success_rate === "number" ? (stats.success_rate * 100).toFixed(0) : "0"}%</strong>
                  </div>
                  <div style={{
                    width: "100%",
                    height: "8px",
                    background: "var(--card-border)",
                    borderRadius: "4px",
                    overflow: "hidden",
                    position: "relative"
                  }}>
                    <div style={{
                      width: `${typeof stats?.success_rate === "number" ? stats.success_rate * 100 : 0}%`,
                      height: "100%",
                      background: typeof stats?.success_rate === "number" ? (stats.success_rate >= 0.7 ? "#22c55e" : stats.success_rate >= 0.4 ? "#f59e0b" : "#ef4444") : "#ef4444",
                      borderRadius: "4px",
                      transition: "width 0.3s ease"
                    }} />
                  </div>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", color: "var(--card-text)" }}>
                  <span>{t("totalRequests")}</span>
                  <strong>{formatNumber(stats?.total_requests)}</strong>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", color: "#ef4444" }}>
                  <span>{t("avgErrorRate")}</span>
                  <strong>{typeof stats?.avg_error_rate === "number" ? stats.avg_error_rate.toFixed(2) : t("notAvailable")}</strong>
                </div>
                {stats && Object.prototype.hasOwnProperty.call(stats, "max_risk_level") && stats.max_risk_level != null && (
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "13px", color: "var(--card-text)" }}>
                    <span>{t("maxRiskLevel")}</span>
                    <span style={{
                      padding: "4px 10px",
                      borderRadius: "999px",
                      fontSize: "11px",
                      fontWeight: 600,
                      color: "white",
                      background: badgeColors[String(stats.max_risk_level).toLowerCase()] || "rgba(100, 116, 139, 0.4)"
                    }}>
                      {levelDisplayNames[stats.max_risk_level] || t("notAvailable")}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </SectionCard>

      <SectionCard title={t("detailedMcpTestResults")} icon={<i className="fas fa-flask"></i>}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "16px" }}>
          {sortedResults.map((test, index) => (
            <div key={test?.test_id || `test-${index}`} style={{
              border: "1px solid var(--card-border)",
              borderRadius: "16px",
              padding: "20px",
              background: "var(--card-bg)",
              display: "flex",
              flexDirection: "column",
              gap: "12px"
            }}>
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                  <span style={{
                    padding: "6px 14px",
                    borderRadius: "999px",
                    fontSize: "12px",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    color: "white",
                    background: test?.passed === true
                      ? "#22c55e"
                      : test?.passed === false
                        ? "#ef4444"
                        : "#6b7280"
                  }}>
                    {test?.passed === true
                      ? t("passed")
                      : test?.passed === false
                        ? t("failed")
                        : test?.passed === null || typeof test?.passed === "undefined"
                          ? t("tbd")
                          : String(test?.passed)}
                  </span>
                  <span style={{
                    padding: "6px 14px",
                    borderRadius: "999px",
                    fontSize: "12px",
                    fontWeight: 600,
                    textTransform: "capitalize",
                    color: "white",
                    background: badgeColors[test?.risk_level] || "rgba(100, 116, 139, 0.4)"
                  }}>
                    {levelDisplayNames[test?.risk_level] || t("unknown")} {t("risk")}
                  </span>
                </div>
                <div style={{ display: "flex", alignItems: "baseline", gap: "12px", flexWrap: "wrap" }}>
                  <div style={{ fontSize: "16px", fontWeight: 600, textTransform: "uppercase", color: "var(--card-text)", letterSpacing: "0.08em", lineHeight: "1" }}>
                    {test?.attack_type ? getSubcategoryLabel("mcp_agent", test.attack_type) : t("unknownAttack")}
                  </div>
                  <div style={{ fontSize: "12px", color: "#9ca3af", lineHeight: "1" }}>
                    {test?.test_id || t("na")}
                  </div>
                </div>
              </div>
              {test?.impact && (
                <div style={{ fontSize: "13px", color: "var(--muted-text)", fontStyle: "italic" }}>
                  {t("impact")}: {test.impact}
                </div>
              )}
              {test?.details && (
                <p style={{ margin: 0, color: "var(--body-text)", lineHeight: 1.6 }}>
                  {test.details}
                </p>
              )}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "12px" }}>
                <MetricChip label={t("durationSeconds")} value={formatNumber(test?.duration_sec)} color="#6366f1" />
                <MetricChip label={t("throughputReqPerSec")} value={formatNumber(test?.throughput_req_per_sec)} color="#14b8a6" />
                <MetricChip label={t("errorRate")} value={typeof test?.error_rate === "number" ? test.error_rate.toFixed(1) : t("notAvailable")} color="#ef4444" />
                <MetricChip label={t("apiErrorResponses")} value={formatNumber(test?.api_error_responses)} color="#f97316" />
                <MetricChip label={t("requestsSent")} value={formatNumber(test?.requests_sent)} color="#3b82f6" />
                <MetricChip label={t("successfullyBlocked")} value={formatNumber(test?.successful)} color="#22c55e" />
                <MetricChip label={t("failedToBlock")} value={formatNumber(test?.failed)} color="#ef4444" />
              </div>
            </div>
          ))}

          {!detailedResults.length && (
            <div style={{ textAlign: "center", padding: "40px", color: "var(--muted-text)" }}>
              {t("noMcpTestExecutions")}
            </div>
          )}
        </div>
      </SectionCard>

      {securityFindings.length > 0 && (
        <SectionCard title={t("securityFindings")} icon={<i className="fas fa-exclamation-triangle"></i>}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "16px" }}>
            {sortedSecurityFindings.map((finding, index) => (
              <div key={finding?.test_id || `finding-${index}`} style={{
                border: "1px solid var(--card-border)",
                borderRadius: "16px",
                padding: "20px",
                background: "var(--card-bg)",
                display: "flex",
                flexDirection: "column",
                gap: "12px"
              }}>
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                    <span style={{
                      padding: "6px 14px",
                      borderRadius: "999px",
                      fontSize: "12px",
                      fontWeight: 600,
                      textTransform: "capitalize",
                      color: "white",
                      background: badgeColors[finding?.risk_level?.toLowerCase()] || "rgba(100, 116, 139, 0.4)"
                    }}>
                      {levelDisplayNames[finding?.risk_level] || t("unknown")} {t("risk")}
                    </span>
                  </div>
                  <div style={{ display: "flex", alignItems: "baseline", gap: "12px", flexWrap: "wrap" }}>
                    <div style={{ fontSize: "16px", fontWeight: 600, textTransform: "uppercase", color: "var(--card-text)", letterSpacing: "0.08em", lineHeight: "1" }}>
                      {finding?.attack_type ? getSubcategoryLabel("mcp_agent", finding.attack_type) : t("unknownAttack")}
                    </div>
                    <div style={{ fontSize: "12px", color: "#9ca3af", lineHeight: "1" }}>
                      {finding?.test_id || t("na")}
                    </div>
                  </div>
                </div>
                {finding?.security_impact && (
                  <div style={{
                    padding: "12px",
                    borderRadius: "8px",
                    background: "rgba(239, 68, 68, 0.08)",
                    border: "1px solid rgba(239, 68, 68, 0.2)"
                  }}>
                    <div style={{ fontSize: "12px", textTransform: "uppercase", color: "#ef4444", fontWeight: 600, marginBottom: "4px", letterSpacing: "0.08em" }}>
                      {t("securityImpact")}
                    </div>
                    <div style={{ fontSize: "14px", color: "var(--card-text)", fontWeight: 500 }}>
                      {finding.security_impact}
                    </div>
                  </div>
                )}
                {finding?.details && (
                  <p style={{ margin: 0, color: "var(--body-text)", lineHeight: 1.6 }}>
                    {finding.details}
                  </p>
                )}
              </div>
            ))}
          </div>
        </SectionCard>
      )}
    </div>
  );
};

const McpServerTestsReportTabDetails = ({ mcpData, keyExists, isEmptyObject, hasExpectedStructure }) => {
  const { t } = useTranslation("reports_recs", { keyPrefix: "reports_detailed.mcp" });

  if (!keyExists || isEmptyObject) {
    return (
      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "60px 40px",
        background: "var(--card-bg)",
        borderRadius: "12px",
        border: "1px solid var(--card-border)",
        marginTop: "20px"
      }}>
        <div style={{
          width: "64px",
          height: "64px",
          borderRadius: "16px",
          background: "rgba(107, 114, 128, 0.15)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: "20px"
        }}>
          <i className="fas fa-file-alt" style={{ fontSize: "28px", color: "#6b7280" }}></i>
        </div>
        <div style={{ fontSize: "18px", color: "var(--card-text)", fontWeight: 600 }}>
          {t("noReportAvailable")}
        </div>
      </div>
    );
  }

  if (!hasExpectedStructure) {
    return (
      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "60px 40px",
        background: "var(--card-bg)",
        borderRadius: "12px",
        border: "1px solid var(--card-border)",
        marginTop: "20px"
      }}>
        <div style={{
          width: "64px",
          height: "64px",
          borderRadius: "16px",
          background: "rgba(239, 68, 68, 0.15)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: "20px"
        }}>
          <i className="fas fa-exclamation-triangle" style={{ fontSize: "28px", color: "#ef4444" }}></i>
        </div>
        <div style={{ fontSize: "18px", color: "var(--card-text)", fontWeight: 600 }}>
          {t("invalidReport")}
        </div>
      </div>
    );
  }

  return <ReportRenderer data={mcpData} />;
};

export default McpServerTestsReportTabDetails;
